import { Type } from "@themes/pollinator/ui/Type";
import { marketingType } from "../../marketing.type";
import { CallToAction } from "@themes/pollinator/ui/CallToAction";
import { Image } from "@themes/pollinator/ui/Image";
import teamPhoto from "@themes/pollinator/assets/images/team-photo.jpg";
import { marketingContentData } from "../../components/Content";

export type Props = {
  /**
   * Offered only where the page still asks for work. The homepage does not:
   * the copy update has no hire-us ask, so it passes nothing and the card
   * stays off.
   */
  onHireUsClick?: () => void;
};

export function MarketingAbout({ onHireUsClick }: Props) {
  return (
    <div className="flex flex-col gap-8 lg:gap-12">
      <Type variant="body1" className={`${marketingType.body} max-w-[833px]`}>
        Collectively, we have been working across art, technology, and social
        impact for decades. And as artists and changemakers ourselves, we’re
        not going anywhere. We’re building Pollinator because it’s the
        thing we’ve always wanted to exist, and now it’s becoming real.
      </Type>
      {/* Between the paragraph and the names, where the design puts it. The
          design crops it to a wide rectangle from a much taller original, so
          the crop is left to object-cover rather than baked into the file. */}
      <Image
        src={teamPhoto}
        alt="The Pollinator team together in a living room"
        className="w-full max-w-[52rem] aspect-[833/567] rounded-2xl overflow-hidden"
        imageClassName="w-full h-full object-cover"
      />
      {/* The technology argument, which the design moved out of Research and
          under the team's photograph: it is read as this team's commitment
          rather than as a claim made about the product. */}
      <div id="technology" className="flex flex-col gap-4 lg:gap-8">
        <Type
          variant="h3"
          as="h3"
          className="text-[20px] leading-[30px] md:text-[20px] lg:text-[36px] md:leading-[30px] lg:leading-[54px] md:tracking-[-0.4px] lg:tracking-[-0.72px] font-medium"
        >
          We leverage research and technology with care, to build
          interdependence between artists, changemakers, organizations, and
          grantmakers.
        </Type>
        <Type variant="body1" className={`${marketingType.body} max-w-[807px]`}>
          There is no other team of artists and engineers working on cutting-edge
          research and tech built specifically for the arts and social impact
          sectors, without ulterior motives. Pollinator exists to support a shift
          away from online networks that foster exhibitionism and consumption
          toward technology that enacts shared governance, ownership, and
          belonging. While we are ambitious about our growth and the potential of
          our platform, we will never sell, rent, or share participant data
          without direct consent. We are not VC-backed and have no desire to be.
        </Type>
      </div>
      <Type variant="h3" as="h3" className={`${marketingType.display} ${marketingType.displayWeight} mt-4 lg:-my-[9px]`}>
        Core Team
      </Type>
      <div className="grid w-full grid-cols-2 gap-x-6 gap-y-6 lg:max-w-[1216px] lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8">
        {marketingContentData.team.map(({ title, description }) => (
          <div key={title} className="flex flex-col">
            <Type variant="body1" as="h4" className={`${marketingType.body} font-bold lg:tracking-[-0.4px] max-w-none`}>
              {title}
            </Type>
            <Type variant="body2" className={`${marketingType.small} max-w-none`}>
              {description}
            </Type>
          </div>
        ))}
      </div>
      {onHireUsClick && (
        <CallToAction
          title="Love Pollinator? Work with us."
          description="We are committed to developing cutting-edge technology for the arts and social impact sectors. Our engineers and directors have worked together for fifteen years as a 'strike team' for corporate clients. Get in touch to learn more about our rates and services."
          primaryButtonText="Hire us"
          onPrimaryClick={onHireUsClick}
          theme="dark"
        />
      )}
    </div>
  );
}

export default MarketingAbout;

